import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './PropertyWizard.css';

const regionNameCollator = new Intl.Collator('fa', {
  numeric: true,
  sensitivity: 'base',
});

const sortRegionsByName = (regions) => [...regions].sort((a, b) =>
  regionNameCollator.compare(a.name ?? '', b.name ?? '')
);

// Searchable select dropdown component
function SearchableSelect({ options, value, onChange, placeholder, disabled, labelKey = 'name', valueKey = 'id' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const selected = options.find(o => String(o[valueKey]) === String(value));

  const filtered = query.trim()
    ? options.filter(o => o[labelKey]?.includes(query.trim()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="searchable-select" ref={containerRef}>
      <button
        type="button"
        className={`searchable-select-trigger form-select${disabled ? ' disabled' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
      >
        <span className={selected ? '' : 'placeholder-text'}>
          {selected ? selected[labelKey] : placeholder}
        </span>
      </button>

      {open && (
        <div className="searchable-select-dropdown">
          <input
            autoFocus
            type="text"
            className="searchable-select-search"
            placeholder="جستجو..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
          <ul className="searchable-select-list">
            <li
              className={`searchable-select-option${!value ? ' active' : ''}`}
              onMouseDown={() => handleSelect('')}
            >
              {placeholder}
            </li>
            {filtered.length === 0 && (
              <li className="searchable-select-empty">موردی یافت نشد</li>
            )}
            {filtered.map(o => (
              <li
                key={o[valueKey]}
                className={`searchable-select-option${String(o[valueKey]) === String(value) ? ' active' : ''}`}
                onMouseDown={() => handleSelect(o[valueKey])}
              >
                {o[labelKey]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// GIS Data State
let provincesData = {};
let citiesData = {};
let regionsData = {};

// Location Step Component
function LocationStep({ onSubmit, initialData = {}, onPreviousStep }) {
  const [address, setAddress] = useState(initialData.address || '');
  const [selectedProvince, setSelectedProvince] = useState(initialData.state || '');
  const [selectedCity, setSelectedCity] = useState(initialData.city || '');
  const [selectedDistrict, setSelectedDistrict] = useState(initialData.district || '');
  const [neighborhood, setNeighborhood] = useState(initialData.neighborhood || '');
  const [postalCode, setPostalCode] = useState(initialData.postal_code || '');
  const [lat, setLat] = useState(initialData.location?.coordinates?.[1]?.toString() || '35.6892'); // Default to Tehran
  const [lon, setLon] = useState(initialData.location?.coordinates?.[0]?.toString() || '51.3890'); // Default to Tehran
  const [selectedPosition, setSelectedPosition] = useState([
    initialData.location?.coordinates?.[1] || 35.6892,
    initialData.location?.coordinates?.[0] || 51.3890
  ]);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState(null);

  // Load provinces from API
  const loadProvinces = async () => {
    if (provinces.length > 0) return provinces; // Already loaded, return existing data

    setLoadingProvinces(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.locations.provinces, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const sorted = [...data].sort((a, b) => (b.population ?? 0) - (a.population ?? 0));
        setProvinces(sorted);
        provincesData = sorted.reduce((acc, province) => {
          acc[province.name] = province;
          return acc;
        }, {});
        return data;
      }
    } catch (error) {

    } finally {
      setLoadingProvinces(false);
    }
    return [];
  };

  // Load cities for a province
  const loadCities = async (provinceId) => {
    setLoadingCities(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.locations.provinceCities(provinceId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const sorted = [...data].sort((a, b) => (b.population ?? 0) - (a.population ?? 0));
        setCities(sorted);
        citiesData = sorted.reduce((acc, city) => {
          acc[city.id] = city;
          return acc;
        }, {});
        return data;
      }
    } catch (error) {

    } finally {
      setLoadingCities(false);
    }
    return [];
  };

  // Load regions for a city - Fixed API endpoint
  const loadRegions = async (cityId) => {
    setLoadingRegions(true);
    try {
      const token = localStorage.getItem('access_token');
      // Use the correct API endpoint - filter regions by city
      const response = await apiRequest(apiRoutes.locations.regionsByCity(cityId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();

        const sorted = sortRegionsByName(data);
        setRegions(sorted);
        regionsData = sorted.reduce((acc, region) => {
          acc[region.id] = region;
          return acc;
        }, {});
        return data;
      } else {
        // Try fallback approach - get all regions and filter client-side

        const fallbackResponse = await apiRequest(apiRoutes.locations.regions, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (fallbackResponse.ok) {
          const allRegions = await fallbackResponse.json();
          const filteredRegions = allRegions.filter(region => region.city === cityId);
          const sortedRegions = sortRegionsByName(filteredRegions);

          setRegions(sortedRegions);
          regionsData = sortedRegions.reduce((acc, region) => {
            acc[region.id] = region;
            return acc;
          }, {});
          return filteredRegions;
        } else {

          // Set empty regions array to avoid showing loading state indefinitely
          setRegions([]);
        }
      }
    } catch (error) {

      // Set empty regions array to avoid showing loading state indefinitely
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
    return [];
  };

  // Load neighborhoods for a region
  const loadNeighborhoods = async (regionId) => {
    setLoadingNeighborhoods(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.locations.regionNeighborhoods(regionId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();

        const sorted = [...data].sort((a, b) => (b.population ?? 0) - (a.population ?? 0));
        setNeighborhoods(sorted);
        return data;
      } else {
        setNeighborhoods([]);
      }
    } catch (error) {

      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
    return [];
  };

  // Find neighborhood by point
  const findNeighborhood = async (lat, lon) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.locations.findNeighborhoodByPoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ lat, lon })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.found) {
          setNeighborhood(data.neighborhood.name);
          return data.neighborhood;
        }
      }
    } catch (error) {

    }
    return null;
  };

  // Handle province selection
  const handleProvinceChange = async (provinceId) => {
    const province = provinces.find(p => p.id === parseInt(provinceId));
    if (!province) return;

    setSelectedProvince(province.name);
    setSelectedProvinceId(province.id);
    setSelectedCity('');
    setSelectedCityId(null);
    setSelectedDistrict('');
    setSelectedRegionId(null);
    setSelectedNeighborhoodId(null);
    setNeighborhood('');
    setCities([]);
    setRegions([]);
    setNeighborhoods([]);

    // Load cities for this province
    await loadCities(provinceId);

    // Update map to province center
    if (province.center) {
      setLat(province.center.lat.toString());
      setLon(province.center.lon.toString());
      setSelectedPosition([province.center.lat, province.center.lon]);
    }
  };

  // Handle city selection
  const handleCityChange = async (cityId) => {
    const city = cities.find(c => c.id === parseInt(cityId));
    if (!city) return;

    setSelectedCity(city.name);
    setSelectedCityId(city.id);
    setSelectedDistrict('');
    setSelectedRegionId(null);
    setSelectedNeighborhoodId(null);
    setNeighborhood('');
    setRegions([]);
    setNeighborhoods([]);

    // Load regions for this city
    await loadRegions(cityId);

    // Update coordinates
    if (city.coordinates) {
      setLat(city.coordinates.lat.toString());
      setLon(city.coordinates.lon.toString());
      setSelectedPosition([city.coordinates.lat, city.coordinates.lon]);
    }
  };

  // Handle district selection
  const handleDistrictChange = (regionId) => {
    const region = regions.find(r => r.id === parseInt(regionId));
    if (!region) return;

    setSelectedDistrict(region.name);
    setSelectedRegionId(region.id);
    setSelectedNeighborhoodId(null);
    setNeighborhood('');
    setNeighborhoods([]);

    // Load neighborhoods for this region
    loadNeighborhoods(regionId);

    // Update coordinates to region center
    if (region.center) {
      setLat(region.center.lat.toString());
      setLon(region.center.lon.toString());
      setSelectedPosition([region.center.lat, region.center.lon]);
    }
  };

  // Handle neighborhood selection
  const handleNeighborhoodChange = (neighborhoodId) => {
    const neighborhood = neighborhoods.find(n => n.id === parseInt(neighborhoodId));
    if (!neighborhood) return;

    setNeighborhood(neighborhood.name);
    setSelectedNeighborhoodId(neighborhood.id);

    // Update coordinates to neighborhood center if available
    if (neighborhood.center) {
      setLat(neighborhood.center.lat.toString());
      setLon(neighborhood.center.lon.toString());
      setSelectedPosition([neighborhood.center.lat, neighborhood.center.lon]);
    }
  };

  // Load provinces on component mount
  useEffect(() => {
    loadProvinces();
  }, []);

  // Update form when initialData changes (when navigating back)
  useEffect(() => {
    const restoreLocationData = async () => {
      setAddress(initialData.address || '');
      setSelectedProvinceId(initialData.province_id || null);
      setSelectedCityId(initialData.city_id || null);
      setSelectedRegionId(initialData.region_id || null);
      setSelectedNeighborhoodId(initialData.neighborhood_id || null);
      setPostalCode(initialData.postal_code || '');
      setLat(initialData.location?.coordinates?.[1]?.toString() || '35.6892');
      setLon(initialData.location?.coordinates?.[0]?.toString() || '51.6892');
      setSelectedPosition([
        initialData.location?.coordinates?.[1] || 35.6892,
        initialData.location?.coordinates?.[0] || 51.6892
      ]);

      // Load the hierarchy when navigating back
      if (initialData.province_id) {
        // Ensure provinces are loaded first
        await loadProvinces();

        // Load cities for the province
        await loadCities(initialData.province_id);

        if (initialData.city_id) {
          // Load regions for the city
          await loadRegions(initialData.city_id);

          if (initialData.region_id) {
            // Load neighborhoods for the region
            await loadNeighborhoods(initialData.region_id);
          }
        }
      }
    };

    if (initialData.province_id || initialData.city_id || initialData.region_id || initialData.neighborhood_id) {
      restoreLocationData();
    }
  }, [initialData]);

  const handleLocationSelect = async (newLat, newLon) => {
    setLat(newLat.toFixed(6));
    setLon(newLon.toFixed(6));
    setSelectedPosition([newLat, newLon]);

    // Get address from coordinates using Nominatim API
    await getAddressFromCoordinates(newLat, newLon);

    // Try to find neighborhood at this location
    const foundNeighborhood = await findNeighborhood(newLat, newLon);

    // Always try to find the corresponding province, city, and region from our database
    // regardless of whether we found a neighborhood
    await findLocationHierarchy(newLat, newLon);
  };

  // Find location hierarchy from coordinates using our database
  const findLocationHierarchy = async (lat, lon) => {
    try {
      const token = localStorage.getItem('access_token');

      // Get all provinces and check which one contains this point
      const provincesResponse = await apiRequest(apiRoutes.locations.provinces, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (provincesResponse.ok) {
        const allProvinces = await provincesResponse.json();

        // Find the closest province
        let closestProvince = null;
        let minDistance = Infinity;

        for (const province of allProvinces) {
          if (province.center) {
            const distance = Math.sqrt(
              Math.pow(province.center.lat - lat, 2) +
              Math.pow(province.center.lon - lon, 2)
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestProvince = province;
            }
          }
        }

        if (closestProvince) {

          // Set the province
          setSelectedProvince(closestProvince.name);
          setSelectedProvinceId(closestProvince.id);

          // Load cities for this province
          const citiesResponse = await apiRequest(apiRoutes.locations.provinceCities(closestProvince.id), {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (citiesResponse.ok) {
            const citiesData = await citiesResponse.json();

            // Find the closest city
            let closestCity = null;
            let minCityDistance = Infinity;

            for (const city of citiesData) {
              if (city.coordinates) {
                const distance = Math.sqrt(
                  Math.pow(city.coordinates.lat - lat, 2) +
                  Math.pow(city.coordinates.lon - lon, 2)
                );
                if (distance < minCityDistance) {
                  minCityDistance = distance;
                  closestCity = city;
                }
              }
            }

            if (closestCity) {

              // Set the city
              setSelectedCity(closestCity.name);
              setSelectedCityId(closestCity.id);
              setCities(citiesData);

              // Load regions for this city
              const regionsResponse = await apiRequest(apiRoutes.locations.regionsByCity(closestCity.id), {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (regionsResponse.ok) {
                const regionsData = await regionsResponse.json();

                // Find the closest region
                let closestRegion = null;
                let minRegionDistance = Infinity;

                for (const region of regionsData) {
                  if (region.center) {
                    const distance = Math.sqrt(
                      Math.pow(region.center.lat - lat, 2) +
                      Math.pow(region.center.lon - lon, 2)
                    );
                    if (distance < minRegionDistance) {
                      minRegionDistance = distance;
                      closestRegion = region;
                    }
                  }
                }

                if (closestRegion) {

                  // Set the region
                  setSelectedDistrict(closestRegion.name);
                  setSelectedRegionId(closestRegion.id);
                  setRegions(regionsData);

                  // Load neighborhoods for this region
                  const neighborhoodsResponse = await apiRequest(apiRoutes.locations.regionNeighborhoods(closestRegion.id), {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });

                  if (neighborhoodsResponse.ok) {
                    const neighborhoodsData = await neighborhoodsResponse.json();

                    // Find the closest neighborhood
                    let closestNeighborhood = null;
                    let minNeighborhoodDistance = Infinity;

                    for (const neighborhood of neighborhoodsData) {
                      if (neighborhood.center) {
                        const distance = Math.sqrt(
                          Math.pow(neighborhood.center.lat - lat, 2) +
                          Math.pow(neighborhood.center.lon - lon, 2)
                        );
                        if (distance < minNeighborhoodDistance) {
                          minNeighborhoodDistance = distance;
                          closestNeighborhood = neighborhood;
                        }
                      }
                    }

                    if (closestNeighborhood) {

                      // Set the neighborhood
                      setNeighborhood(closestNeighborhood.name);
                      setSelectedNeighborhoodId(closestNeighborhood.id);
                      setNeighborhoods(neighborhoodsData);
                    } else {

                      setNeighborhoods(neighborhoodsData);
                    }
                  } else {

                    setNeighborhoods([]);
                  }
                } else {

                  setRegions(regionsData);
                }
              } else {

                setRegions([]);
              }
            } else {

              setCities(citiesData);
            }
          } else {

            setCities([]);
          }
        } else {

        }
      }
    } catch (error) {

    }
  };

  // Get full address from coordinates using reverse geocoding - Modified to not reset select boxes
  const getAddressFromCoordinates = async (lat, lon) => {
    try {
      const response = await apiRequest(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=fa&addressdetails=1&zoom=18`
      );
      const data = await response.json();

      if (data && data.address) {
        const addressComponents = parseNominatimAddress(data);

        // Only update the address field, don't reset the select boxes
        setAddress(addressComponents.fullAddress || '');
        setPostalCode(addressComponents.postcode || '');
      }
    } catch (error) {

    }
  };

  const handleAddressSearch = async () => {
    if (!searchAddress.trim()) return;

    setSearching(true);
    try {
      // Use Persian geocoding with country code for Iran
      const response = await apiRequest(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&countrycodes=ir&accept-language=fa&limit=1&addressdetails=1`
      );
      const data = await response.json();

      if (data.length > 0) {
        const result = data[0];
        const newLat = parseFloat(result.lat);
        const newLon = parseFloat(result.lon);

        setLat(newLat.toFixed(6));
        setLon(newLon.toFixed(6));
        setSelectedPosition([newLat, newLon]);

        // Parse address components from Nominatim response
        const addressComponents = parseNominatimAddress(result);

        // Update address fields
        setAddress(addressComponents.fullAddress || '');
        setPostalCode(addressComponents.postcode || '');

        // Try to find the location hierarchy from our database
        await findLocationHierarchy(newLat, newLon);
      }
    } catch (error) {

    } finally {
      setSearching(false);
    }
  };

  // Parse Nominatim address response to extract Persian components
  const parseNominatimAddress = (result) => {
    const address = result.address || {};

    // Build full address string
    const addressParts = [];

    // Add house number if available
    if (address.house_number) {
      addressParts.push(`پلاک ${address.house_number}`);
    }

    // Add street/road
    if (address.road) {
      addressParts.push(address.road);
    }

    // Add neighborhood/suburb
    if (address.suburb) {
      addressParts.push(address.suburb);
    }

    // Add district/county
    if (address.county) {
      addressParts.push(address.county);
    }

    // Add city
    if (address.city || address.town || address.village) {
      addressParts.push(address.city || address.town || address.village);
    }

    // Add state/province
    if (address.state || address.province) {
      addressParts.push(address.state || address.province);
    }

    // Add postal code
    if (address.postcode) {
      addressParts.push(`کد پستی: ${address.postcode}`);
    }

    const fullAddress = addressParts.join('، ');

    return {
      fullAddress: fullAddress,
      street: address.road || address.street || '',
      city: address.city || address.town || address.village || '',
      state: address.state || address.province || '',
      district: address.county || address.district || '',
      neighborhood: address.suburb || address.neighbourhood || '',
      postcode: address.postcode || '',
      country: address.country || '',
      houseNumber: address.house_number || ''
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields before submission
    if (!selectedCityId || !selectedProvinceId || !address || !postalCode) {
      alert('لطفاً تمام فیلدهای الزامی را پر کنید');
      return;
    }

    // Compose the data as expected by the backend PropertyLocationSerializer
    onSubmit({
      address,
      city_id: selectedCityId,
      province_id: selectedProvinceId,
      region_id: selectedRegionId,
      neighborhood_id: selectedNeighborhoodId,
      postal_code: postalCode,
      location: {
        type: "Point",
        coordinates: [parseFloat(lon), parseFloat(lat)]
      }
    });
  };

  return (
    <div className="location-step">
      {/* Search and Map Container */}
      <div className="search-map-container">
        {/* Left Column: Search and Form */}
        <div className="left-column">
          {/* Address Search Section */}
          <div className="search-section">
            <h4> جستجوی آدرس</h4>
            <div className="search-input-group">
              <input
                type="text"
                value={searchAddress}
                onChange={e => setSearchAddress(e.target.value)}
                placeholder="آدرس کامل شامل شهر،محله،خیابان و... را وارد کنید (مثال: تهران، خیابان ولیعصر، پلاک ۱۰)"
                className="search-input"
                onKeyPress={e => e.key === 'Enter' && handleAddressSearch()}
                disabled={searching}
              />
              <button
                type="button"
                onClick={handleAddressSearch}
                disabled={searching || !searchAddress.trim()}
                className="search-button"
              >
                {searching ? '⏳ در حال جستجو...' : 'جستجو'}
              </button>
            </div>
          </div>

          {/* Form Section */}
          <div className="form-section">
            <h4>اطلاعات آدرس</h4>

            <form onSubmit={handleSubmit}>
              {/* Address Hierarchy */}
              <div className="address-hierarchy">
                <div className="form-group">
                  <label>استان</label>
                  <SearchableSelect
                    options={provinces}
                    value={selectedProvinceId || ''}
                    onChange={v => v ? handleProvinceChange(v) : null}
                    placeholder={loadingProvinces ? '⏳ در حال بارگذاری...' : 'استان را انتخاب کنید:'}
                    disabled={loadingProvinces}
                  />
                </div>

                <div className="form-group">
                  <label>شهر</label>
                  <SearchableSelect
                    options={cities}
                    value={selectedCityId || ''}
                    onChange={v => v ? handleCityChange(v) : null}
                    placeholder={loadingCities ? '⏳ در حال بارگذاری...' : cities.length === 0 ? 'شهری یافت نشد' : 'شهر را انتخاب کنید:'}
                    disabled={!selectedProvince || loadingCities}
                  />
                </div>

                <div className="form-group">
                  <label>منطقه/شهرستان</label>
                  <SearchableSelect
                    options={regions}
                    value={selectedRegionId || ''}
                    onChange={v => v ? handleDistrictChange(v) : null}
                    placeholder={loadingRegions ? '⏳ در حال بارگذاری...' : regions.length === 0 ? 'منطقه‌ای یافت نشد' : 'منطقه را انتخاب کنید:'}
                    disabled={!selectedCity || loadingRegions}
                  />
                </div>
                <div className="form-group">
                  <label>محله</label>
                  <SearchableSelect
                    options={neighborhoods}
                    value={selectedNeighborhoodId || ''}
                    onChange={v => v ? handleNeighborhoodChange(v) : null}
                    placeholder={loadingNeighborhoods ? '⏳ در حال بارگذاری...' : neighborhoods.length === 0 ? 'محله‌ای یافت نشد' : 'محله را انتخاب کنید:'}
                    disabled={!selectedRegionId || loadingNeighborhoods}
                  />
                </div>
              </div>
              {/* Street Address and Postal Code */}
              <div className="address-details">
                <div className="form-group address-group">
                  <label>آدرس کامل</label>
                  <textarea
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    required
                    rows={2}
                    className="form-textarea"
                    placeholder="آدرس به صورت خودکار از نقشه یا جستجو پر می‌شود و شما می‌توانید آن را ویرایش کنید..."
                  />
                </div>
                <div className="form-group postal-group">
                  <div className="postal-details">
                    <label>کد پستی</label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={e => setPostalCode(e.target.value)}
                      required
                      className="form-input"
                      placeholder="کد پستی را وارد کنید"
                    />
                  </div>
                </div>
              </div>

            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <h4>انتخاب موقعیت روی نقشه</h4>

          <div className="map-container">
            <MapContainer
              center={selectedPosition}
              zoom={13}
              className="map"
              key={`${selectedPosition[0]}-${selectedPosition[1]}`}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <MapClickHandler onLocationSelect={handleLocationSelect} />
              {selectedPosition && (
                <Marker position={selectedPosition} />
              )}
            </MapContainer>
          </div>

          <p className="map-hint">
            روی نقشه کلیک کنید تا موقعیت ملک را انتخاب کنید.
          </p>
        </div>
      </div>

      {/* Action Buttons Container */}
      <div className="action-buttons-container">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
          className="submit-button"
        >
          تأیید و ادامه
        </button>
        <button
          type="button"
          onClick={onPreviousStep}
          className="previous-button"
        >
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default LocationStep;
